import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button
} from '@mui/material';
import {
  MoneyOff as DebtIcon,
  Warning as WarningIcon,
  Schedule as OverdueIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDebtNotifications } from '../../hooks/useDebtNotifications';
import { formatCurrency } from '../../utils/formatters';

interface DebtSummaryCardProps {
  elevation?: number;
}

const DebtSummaryCard: React.FC<DebtSummaryCardProps> = ({ elevation = 3 }) => {
  const navigate = useNavigate();
  const { summary, loading, error, refresh } = useDebtNotifications(2 * 60 * 1000); // تحديث كل دقيقتين

  const handleViewDebts = () => {
    navigate('/admin/debts');
  };

  const getStatusColor = () => {
    if (summary.overdueCount > 0) return 'error';
    if (summary.dueSoonCount > 0) return 'warning';
    if (summary.totalCount > 0) return 'info';
    return 'success';
  };

  const getStatusMessage = () => {
    if (summary.overdueCount > 0) {
      return `${summary.overdueCount} دين متأخر يحتاج متابعة فورية`;
    }
    if (summary.dueSoonCount > 0) {
      return `${summary.dueSoonCount} دين قريب الاستحقاق`;
    }
    if (summary.totalCount > 0) {
      return `${summary.totalCount} إشعار ديون نشط`;
    }
    return 'جميع الديون تحت السيطرة';
  };

  if (loading) {
    return (
      <Card elevation={elevation}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DebtIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">ملخص الديون</Typography>
          </Box>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            جاري تحميل بيانات الديون...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={elevation}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DebtIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6">ملخص الديون</Typography>
          </Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            onClick={refresh}
            startIcon={<RefreshIcon />}
            fullWidth
          >
            إعادة المحاولة
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={elevation}
      sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: `1px solid ${summary.overdueCount > 0 ? '#f44336' : summary.dueSoonCount > 0 ? '#ff9800' : '#e0e0e0'}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DebtIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              💰 ملخص الديون
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="تحديث">
              <IconButton size="small" onClick={refresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="عرض جميع الديون">
              <IconButton size="small" onClick={handleViewDebts}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Status Alert */}
        <Alert 
          severity={getStatusColor()} 
          sx={{ mb: 2, borderRadius: 2 }}
          icon={summary.overdueCount > 0 ? <WarningIcon /> : undefined}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {getStatusMessage()}
          </Typography>
        </Alert>

        {/* Statistics */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              إجمالي الإشعارات
            </Typography>
            <Chip 
              label={summary.totalCount} 
              size="small" 
              color={summary.totalCount > 0 ? 'primary' : 'default'}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          
          {summary.totalCount > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <List dense sx={{ py: 0 }}>
                {summary.overdueCount > 0 && (
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <OverdueIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          ديون متأخرة: <strong>{summary.overdueCount}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {summary.dueSoonCount > 0 && (
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <WarningIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          قريبة الاستحقاق: <strong>{summary.dueSoonCount}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {summary.highAmountCount > 0 && (
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <TrendingUpIcon color="info" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          عالية القيمة: <strong>{summary.highAmountCount}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {summary.newDebtCount > 0 && (
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <DebtIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          ديون جديدة: <strong>{summary.newDebtCount}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </Box>

        {/* Action Button */}
        <Button
          variant={summary.overdueCount > 0 ? 'contained' : 'outlined'}
          color={summary.overdueCount > 0 ? 'error' : 'primary'}
          fullWidth
          onClick={handleViewDebts}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            py: 1
          }}
        >
          {summary.overdueCount > 0 ? 'متابعة الديون المتأخرة' : 'عرض جميع الديون'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DebtSummaryCard;
